import Grid from "@mui/material/Grid2";
import { ColumnProps, FormGroup, Table } from "@cofano/cck";
import { useFrontendTable } from "components/table";
import { ContentProps } from "@cofano/cck/Table/Table";
import { Box, Tooltip, Typography } from "@mui/material";
import { intl } from "LocaleWrapper";
import { CalculationMethod } from "view/calculationmethods/types/calculationmethod";
import { Fragment, ReactNode } from "react";
import { Session } from "ezStore";
import { DataFormat } from "utils";
import { TierUnit, TierPriceUnit } from "__generated__/graphql";
import { CalculationMethodDetailsByTypeProps } from "@schemas/calculationmethods/schema";
import { InfoOutlined, TableRowsOutlined } from "@mui/icons-material";
import { WaterLevelLink } from "utils/LinkUtils";
import useRequest from "components/hooks/useRequest";
import { WaterLevel } from "view/waterlevels/types/waterlevel";
import { CurrencyFragment } from "__generated__/graphql/fragments.generated";


type DetailsField = {
	id: string;
	render: () => ReactNode;
}

type TierProps = {
	calculationMethod: CalculationMethod;
	currency: CurrencyFragment;
	session: Session;
}

const TieredCalculationMethodDetails = ({ calculationMethod, currency, session, hiddenFields }: CalculationMethodDetailsByTypeProps) => {
	const [ measuringPoints ] = useRequest<WaterLevel[]>({ defaultResponse: [], url: "waterlevels/measuring-points" });

	const TieredCalculationMethodDetailsFields: DetailsField[] = [
		{
			id: "calculationMethod.payableOver",
			render: () =>  calculationMethod.tierUnit === TierUnit.CargoQuantity && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "PAYABLE_OVER" })} overflow="hidden">
						{intl.formatMessage({ id: `PAYABLE_OVER_${calculationMethod.payableOver}` })}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.waterLevelLocation",
			render: () => calculationMethod.tierUnit === TierUnit.WaterLevel && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "LOCATION" })} overflow="hidden">
						<WaterLevelLink name={measuringPoints?.find(x => x.id === calculationMethod.waterLevelLocation)?.name} />
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.referenceRateDateType",
			render: () => calculationMethod.tierUnit === TierUnit.GasoilPrice && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_DATE_TYPE" })} overflow="hidden">
						{intl.formatMessage({ id: `REFERENCE_RATE_DATE_TYPE_${calculationMethod.referenceRateDateType}` })}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.cdniCostIncluded",
			render: () => calculationMethod.tierUnit === TierUnit.GasoilPrice && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "CDNI_COST_LABEL" })} overflow="hidden">
						{intl.formatMessage({ id: calculationMethod.cdniCostIncluded ? "CDNI_COST_INCLUDED_TEXT" : "CDNI_COST_EXCLUDED_TEXT" })}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.applicableAgreements",
			render: () => calculationMethod.applicableAgreements && calculationMethod.applicableAgreements.length > 0 && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "APPLICABLE_AGREEMENTS" })} overflow="hidden">
						{calculationMethod.applicableAgreements.map(a => intl.formatMessage({ id: `AGREEMENT_TYPE_${a.type}` })).join(", ")}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.tiers",
			render: () => (
				<Tiers calculationMethod={calculationMethod} currency={currency} session={session} />
			),
		},
	];

	return (
		<>
			{TieredCalculationMethodDetailsFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

const Tiers = ({ calculationMethod, currency, session }: TierProps) => {
	const { tableProps } = useFrontendTable({
		id: "calculationMethodTiers",
		defaultSize: "COMPACT",
		content: calculationMethod.tiers as ContentProps,
		saveForOrganizationEnabled: false,
	});

	const columns: ColumnProps[] = [
		{
			id: "calculationMethod.tiers.minimum",
			title: intl.formatMessage({ id: `TIER_MINIMUM_${calculationMethod.tierUnit}` }),
			render: (row) => calculationMethod.tierUnit === TierUnit.CargoQuantity ? `${row.minimum} ${DataFormat.UNITS.MTON}` : calculationMethod.tierUnit === TierUnit.WaterLevel ? `${row.minimum} m` : calculationMethod.tierUnit === TierUnit.GasoilPrice ? DataFormat.price(row.minimum, currency.isoCode, session.locale) : row.minimum,
		}, {
			id: "calculationMethod.tiers.maximum",
			title: intl.formatMessage({ id: `TIER_MAXIMUM_${calculationMethod.tierUnit}` }),
			render: (row) => calculationMethod.tierUnit === TierUnit.CargoQuantity ? `${row.maximum} ${DataFormat.UNITS.MTON}` : calculationMethod.tierUnit === TierUnit.WaterLevel ? `${row.maximum} m` : calculationMethod.tierUnit === TierUnit.GasoilPrice ? DataFormat.price(row.maximum, currency.isoCode, session.locale) : row.maximum,
		}, {
			id: "calculationMethod.tiers.priceUnit",
			title: intl.formatMessage({ id: "TIER_PRICE_UNIT" }),
			render: (row) => row.priceUnit ? intl.formatMessage({ id: `TIER_PRICE_UNIT_${row.priceUnit}` }) : "",
		}, {
			id: "calculationMethod.tiers.price",
			title: intl.formatMessage({ id: "PRICE" }),
			render: row => {
				const formatted = DataFormat.price(row.price, currency.isoCode, session.locale);
				if (row.priceUnit === TierPriceUnit.VesselDraftInMeters) return `${row.price} m`;
				if (row.priceUnit === TierPriceUnit.SurchargePercentage) return `${row.price}%`;
				if (row.priceUnit === TierPriceUnit.PerUnit && calculationMethod.tierUnit === TierUnit.CargoQuantity) return `${formatted} ${intl.formatMessage({ id: "UNIT_PER" })} ${DataFormat.UNITS.MTON}`;
				return formatted;
			},
		}, {
			id: "calculationMethod.tiers.pricePerMtAdditionalToLumpsum",
			title: <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
				{intl.formatMessage({ id: "TIER_PRICE_PER_MT" })}
				<Tooltip title={intl.formatMessage({ id: "TIER_PRICE_PER_MT_ADDITIONAL_INFO" })}>
					<InfoOutlined fontSize="small" />
				</Tooltip>
			</Box>,
			defaultVisible: calculationMethod.tierUnit === TierUnit.CargoQuantity,
			render: row => row.pricePerMtAdditionalToLumpsum ? DataFormat.price(row.pricePerMtAdditionalToLumpsum, currency.isoCode, session.locale) : "",
			width: 180,
			disableResizing: true,
			disableFlexGrow: true,
		},
	];

	return (
		<Grid container size={12}>
			<Grid size={6}>
				<Box sx={{  display: "flex", alignItems: "center" }}>
					<TableRowsOutlined fontSize="small" />
					<Typography color="textPrimary"
						fontSize={13}
						fontWeight="medium"
						marginLeft={1}>
						{intl.formatMessage({ id: "TIERS" })}
					</Typography>
				</Box>
			</Grid>
			<Grid size={12}>
				<Table
					{...tableProps}
					disableFooter
					columns={columns} />
			</Grid>
		</Grid>
	);
};

export default TieredCalculationMethodDetails;
